import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { formatDate, toLocalISODate } from '../lib/formatUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Users, FolderKanban, CheckSquare, TicketCheck, 
  DollarSign, AlertTriangle, Clock, TrendingUp,
  Globe, Server, Shield, FileText, CalendarDays,
  Receipt, UserPlus, Plus, Briefcase, ArrowRight,
  LogIn, LogOut, Megaphone, ArrowUpRight
} from 'lucide-react';
import { TransactionFormDialog } from '../components/transactions/TransactionFormDialog';

const CHART_COLORS = {
  primary: '#FF4500',
  secondary: '#4682B4',
  accent: '#FFD700',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#8B5CF6',
  muted: '#52525B',
};

const DATE_RANGES = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '3m', label: 'Last 3 Months' },
  { value: '6m', label: 'Last 6 Months' },
  { value: '1y', label: 'Last 12 Months' },
];

const ChartTooltipContent = ({ active, payload, label, isCurrency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-xl">
      <p className="text-xs font-medium text-muted-foreground mb-1.5">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="h-2 w-2 rounded-full" style={{ background: entry.color }} />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-semibold text-foreground">
            {isCurrency ? `₹${(entry.value / 1000).toFixed(0)}K` : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const PieTooltipContent = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-xl">
      <div className="flex items-center gap-2 text-sm">
        <span className="h-2 w-2 rounded-full" style={{ background: d.payload.color }} />
        <span className="font-medium">{d.name}</span>
        <span className="text-muted-foreground">— {d.value}</span>
      </div>
    </div>
  );
};

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, hasPermission } = useAuth();
  const isEmployee = user?.is_employee;
  const canViewClients = hasPermission('clients.view');
  const canViewProjects = hasPermission('projects.view');
  const canViewInvoices = hasPermission('invoices.view');
  const showBusinessMetrics = !isEmployee || (canViewClients && canViewProjects && canViewInvoices);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('6m');
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [txnDialogOpen, setTxnDialogOpen] = useState(false);
  const [payrollNotice, setPayrollNotice] = useState(null);

  useEffect(() => {
    if (searchParams.get('access_denied')) {
      toast.error('Access denied. You do not have permission to view that page.');
      searchParams.delete('access_denied');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchDashboardData = React.useCallback(async (range) => {
    try {
      setLoading(true);
      const response = await api.get(`/dashboard?range=${range}`);
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch {
      // Network/auth error — keep previous data so the UI doesn't flicker to empty.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData(dateRange);
  }, [dateRange, fetchDashboardData]);

  useEffect(() => {
    fetchTodayAttendance();
    fetchUpcomingHolidays();
    api.get('/my/payroll-verification-notice')
      .then(r => { if (r.data?.success) setPayrollNotice(r.data.data); })
      .catch(() => setPayrollNotice(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      const res = await api.get('/attendance/today');
      if (res.data.success) setTodayAttendance(res.data.data);
    } catch {}
  };

  const fetchUpcomingHolidays = async () => {
    try {
      const year = new Date().getFullYear();
      const res = await api.get(`/holidays?year=${year}`);
      if (res.data.success) {
        const today = toLocalISODate();
        const upcoming = res.data.data
          .filter(h => h.date >= today)
          .slice(0, 5);
        setUpcomingHolidays(upcoming);
      }
    } catch {}
  };

  // Real backend-driven series — falls back to empty arrays while loading.
  const dash = dashboardData || {};
  const stats = dash.stats || {};
  const tasksByStatus = dash.tasks_by_status || { todo: 0, in_progress: 0, review: 0, done: 0, overdue: 0 };
  const projectsByStatus = dash.projects_by_status || { active: 0, completed: 0, planning: 0, on_hold: 0 };

  const revenueData = useMemo(() =>
    (dash.revenue_data || []).map(r => ({ month: r.month, invoiced: r.invoiced || 0, received: r.received || 0 })),
    [dash.revenue_data],
  );

  const taskData = useMemo(() => [
    { status: 'To Do',       count: tasksByStatus.todo || 0,        fill: CHART_COLORS.secondary },
    { status: 'In Progress', count: tasksByStatus.in_progress || 0, fill: CHART_COLORS.primary },
    { status: 'Review',      count: tasksByStatus.review || 0,      fill: CHART_COLORS.accent },
    { status: 'Done',        count: tasksByStatus.done || 0,        fill: CHART_COLORS.success },
    { status: 'Overdue',     count: tasksByStatus.overdue || 0,     fill: CHART_COLORS.danger },
  ], [tasksByStatus]);

  const projectStatusData = useMemo(() => [
    { name: 'In Progress', value: projectsByStatus.active || 0,    color: CHART_COLORS.primary },
    { name: 'Planning',    value: projectsByStatus.planning || 0,  color: CHART_COLORS.warning },
    { name: 'Completed',   value: projectsByStatus.completed || 0, color: CHART_COLORS.success },
    { name: 'On Hold',     value: projectsByStatus.on_hold || 0,   color: CHART_COLORS.muted },
  ], [projectsByStatus]);

  const recentProjects = dash.recent_projects || [];
  const upcomingDeadlines = dash.upcoming_deadlines || [];
  const expiringAssets = dash.expiring_assets || [];

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const getStatusBadge = (status) => {
    const cfg = {
      in_progress: { label: 'In Progress', className: 'bg-blue-500/10 text-blue-500' },
      planning: { label: 'Planning', className: 'bg-amber-500/10 text-amber-500' },
      completed: { label: 'Completed', className: 'bg-emerald-500/10 text-emerald-500' },
      on_hold: { label: 'On Hold', className: 'bg-gray-500/10 text-gray-500' },
    };
    const c = cfg[status] || cfg.planning;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const cfg = {
      critical: { label: 'Critical', className: 'bg-red-500/10 text-red-500' },
      high: { label: 'High', className: 'bg-orange-500/10 text-orange-500' },
      medium: { label: 'Medium', className: 'bg-amber-500/10 text-amber-500' },
      low: { label: 'Low', className: 'bg-gray-500/10 text-gray-500' },
    };
    const c = cfg[priority] || cfg.medium;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getAssetIcon = (type) => {
    const icons = {
      domain: <Globe className="h-4 w-4 text-blue-500" />,
      ssl: <Shield className="h-4 w-4 text-green-500" />,
      hosting: <Server className="h-4 w-4 text-purple-500" />,
      amc: <FileText className="h-4 w-4 text-orange-500" />,
    };
    return icons[type] || <FileText className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header with Date Range Filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Overview of your business metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[160px] h-9" data-testid="date-range-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGES.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Payroll Verification Notice — visible only while last month's salary is unpaid */}
      {payrollNotice?.show && (
        <div
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3.5 flex items-start gap-3.5"
          data-testid="payroll-verification-notice"
        >
          <div className="h-9 w-9 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
            <Megaphone className="h-4.5 w-4.5 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-200">
              Verify your attendance for <span className="underline decoration-amber-500/50">{payrollNotice.prev_month_label}</span>
            </p>
            <p className="text-xs text-amber-200/85 mt-1 leading-relaxed">
              Salaries for {payrollNotice.prev_month_label} are released by the {payrollNotice.payroll_release_day}<sup>th</sup> of this month.
              Please review your check-in/check-out logs and EOD notes for last month and raise corrections with HR <strong>before payroll is generated</strong> —
              <strong className="text-amber-100"> no further adjustments will be accepted once salaries are paid out.</strong>
            </p>
          </div>
          <button
            onClick={() => navigate('/my-portal/attendance')}
            className="self-center shrink-0 text-xs font-medium text-amber-300 hover:text-amber-100 underline decoration-dotted underline-offset-2 flex items-center gap-1"
            data-testid="payroll-notice-cta"
          >
            Review attendance
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Stats Grid */}
      {showBusinessMetrics && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card" data-testid="stat-clients">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Clients</p>
                <p className="text-3xl font-bold mt-1">{stats.clients || 0}</p>
                <p className="text-xs text-emerald-500 mt-1">{stats.clients || 0} active</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="stat-projects">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Projects</p>
                <p className="text-3xl font-bold mt-1">{projectsByStatus.active || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">of {stats.projects || 0} total</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-secondary/10 flex items-center justify-center">
                <FolderKanban className="h-6 w-6 text-secondary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="stat-tasks">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open Tasks</p>
                <p className="text-3xl font-bold mt-1">{(tasksByStatus.todo || 0) + (tasksByStatus.in_progress || 0) + (tasksByStatus.review || 0)}</p>
                {tasksByStatus.overdue > 0 ? (
                  <p className="text-xs text-destructive mt-1" data-testid="stat-tasks-overdue">{tasksByStatus.overdue} overdue</p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">{tasksByStatus.done || 0} done</p>
                )}
              </div>
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center">
                <CheckSquare className="h-6 w-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="stat-revenue">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(stats.pending_amount || 0)}</p>
                <p className="text-xs text-emerald-500 mt-1">{formatCurrency(stats.received_in_range || 0)} received</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Today's Overview - Attendance & Upcoming Holidays */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Attendance Status */}
        <Card data-testid="today-attendance-widget">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Today's Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayAttendance ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                  <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <LogIn className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Checked In</p>
                    <p className="text-xl font-bold" data-testid="checkin-time">{todayAttendance.check_in || '--:--'}</p>
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-500">Active</Badge>
                </div>
                <div className={`flex items-center gap-4 p-4 rounded-lg ${todayAttendance.check_out ? 'bg-blue-500/5 border border-blue-500/20' : 'bg-muted/20 border border-border'}`}>
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${todayAttendance.check_out ? 'bg-blue-500/10' : 'bg-muted/30'}`}>
                    <LogOut className={`h-5 w-5 ${todayAttendance.check_out ? 'text-blue-500' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Checked Out</p>
                    <p className="text-xl font-bold" data-testid="checkout-time">{todayAttendance.check_out || 'In Progress'}</p>
                  </div>
                  {todayAttendance.total_hours != null && (
                    <Badge className="bg-blue-500/10 text-blue-500">{todayAttendance.total_hours}h</Badge>
                  )}
                </div>
                {(todayAttendance.is_holiday || todayAttendance.is_weekend) && (
                  <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-sm text-amber-500 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {todayAttendance.is_holiday
                      ? `Working on holiday: ${todayAttendance.holiday_name}`
                      : 'Working on weekend'}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <Clock className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">No attendance recorded today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Holidays */}
        <Card data-testid="upcoming-holidays-widget">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-amber-500" />
                Upcoming Holidays
              </CardTitle>
              <button
                onClick={() => navigate('/holidays')}
                className="text-xs text-primary hover:underline flex items-center gap-1"
                data-testid="view-all-holidays"
              >
                View All <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {upcomingHolidays.length > 0 ? (
              <div className="space-y-3">
                {upcomingHolidays.map(h => {
                  const d = new Date(h.date + 'T00:00:00');
                  const dayName = d.toLocaleDateString('en-IN', { weekday: 'short' });
                  const dateStr = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                  const now = new Date();
                  const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={h.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors" data-testid={`upcoming-holiday-${h.id}`}>
                      <div className="h-12 w-12 rounded-lg bg-amber-500/10 flex flex-col items-center justify-center shrink-0">
                        <span className="text-[10px] font-semibold text-amber-500 uppercase leading-none">{dayName}</span>
                        <span className="text-sm font-bold text-amber-500 leading-tight">{d.getDate()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{h.name}</p>
                        <p className="text-xs text-muted-foreground">{dateStr}</p>
                      </div>
                      <Badge className={diffDays <= 7 ? 'bg-amber-500/10 text-amber-500' : 'bg-muted text-muted-foreground'}>
                        {diffDays === 0 ? 'Today' : diffDays === 1 ? 'Tomorrow' : `${diffDays}d`}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <CalendarDays className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">No upcoming holidays this year</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      {(() => {
        const quickActions = [
          { label: 'New Invoice', icon: Receipt, path: '/invoices', color: 'text-emerald-500', bg: 'bg-emerald-500/10', testId: 'qa-new-invoice', permission: 'invoices.create' },
          { label: 'Add Employee', icon: UserPlus, path: '/employees', color: 'text-blue-500', bg: 'bg-blue-500/10', testId: 'qa-add-employee', permission: 'employees.create' },
          { label: 'Log Transaction', icon: DollarSign, action: 'open-txn-dialog', color: 'text-amber-500', bg: 'bg-amber-500/10', testId: 'qa-log-expense', permission: 'invoices.create' },
          { label: 'New Task', icon: CheckSquare, path: '/tasks', color: 'text-primary', bg: 'bg-primary/10', testId: 'qa-new-task', permission: 'tasks.create' },
          { label: 'New Ticket', icon: TicketCheck, path: '/tickets', color: 'text-purple-500', bg: 'bg-purple-500/10', testId: 'qa-new-ticket', permission: 'tickets.create' },
          { label: 'Add Client', icon: Briefcase, path: '/clients', color: 'text-cyan-500', bg: 'bg-cyan-500/10', testId: 'qa-add-client', permission: 'clients.create' },
        ].filter(action => hasPermission(action.permission));
        if (quickActions.length === 0) return null;
        return (
          <Card data-testid="quick-actions">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {quickActions.map(action => (
                  <button
                    key={action.label}
                    onClick={() => {
                      if (action.action === 'open-txn-dialog') setTxnDialogOpen(true);
                      else navigate(action.path);
                    }}
                    className="group flex flex-col items-center gap-2.5 p-4 rounded-xl bg-muted/20 hover:bg-muted/40 border border-transparent hover:border-primary/20 transition-all duration-200"
                    data-testid={action.testId}
                  >
                    <div className={`h-11 w-11 rounded-full ${action.bg} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <action.icon className={`h-5 w-5 ${action.color}`} />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">{action.label}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Charts Row */}
      {showBusinessMetrics && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Trend */}
        <Card className="lg:col-span-2" data-testid="revenue-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              Revenue Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradInvoiced" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.secondary} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLORS.secondary} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradReceived" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="month" tick={{ fill: '#a1a1aa', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#a1a1aa', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v / 1000}K`} />
                  <Tooltip content={<ChartTooltipContent isCurrency />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Area type="monotone" dataKey="invoiced" name="Invoiced" stroke={CHART_COLORS.secondary} fill="url(#gradInvoiced)" strokeWidth={2} />
                  <Area type="monotone" dataKey="received" name="Received" stroke={CHART_COLORS.success} fill="url(#gradReceived)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Project Status Pie */}
        <Card data-testid="project-status-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FolderKanban className="h-5 w-5 text-primary" />
              Project Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[220px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={projectStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={4}
                    dataKey="value"
                    stroke="none"
                  >
                    {projectStatusData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {projectStatusData.map((item) => (
                <div key={item.name} className="flex items-center gap-2 text-xs">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: item.color }} />
                  <span className="text-muted-foreground truncate">{item.name}</span>
                  <span className="font-semibold ml-auto">{item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Task Distribution Bar + Recent Projects */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Task Distribution */}
        <Card data-testid="task-distribution-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-accent" />
              Tasks by Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={taskData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="status" tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#a1a1aa', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltipContent />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                  <Bar dataKey="count" name="Tasks" radius={[4, 4, 0, 0]} barSize={36}>
                    {taskData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Projects */}
        {showBusinessMetrics && (
        <Card className="lg:col-span-2" data-testid="recent-projects">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FolderKanban className="h-5 w-5 text-primary" />
              Recent Projects
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentProjects.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No active projects</p>
              ) : recentProjects.map((project, index) => (
                <Link
                  key={project.id || index}
                  to={`/projects/${project.id}`}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer"
                  data-testid={`recent-project-${index}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{project.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{project.client_name || '—'}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-32">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium">{project.progress_percentage || 0}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary transition-all duration-500" style={{ width: `${project.progress_percentage || 0}%` }} />
                      </div>
                    </div>
                    {getStatusBadge(project.status === 'active' ? 'in_progress' : project.status)}
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Bottom Row */}
      {showBusinessMetrics && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Deadlines */}
        <Card data-testid="upcoming-deadlines">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Upcoming Deadlines
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingDeadlines.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No deadlines in the next 30 days</p>
              ) : upcomingDeadlines.map((item, index) => (
                <div
                  key={`${item.type}-${item.id || index}`}
                  className="p-3 rounded-lg bg-muted/20 border-l-2 border-amber-500 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => item.type === 'project' ? navigate(`/projects/${item.id}`) : navigate('/tasks')}
                  data-testid={`deadline-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {item.type === 'project' ? '📁' : '✓'} {item.project_name || '—'}
                      </p>
                    </div>
                    {getPriorityBadge(item.priority)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Due: {formatDate(item.due_date)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Expiring Assets */}
        <Card data-testid="expiring-assets">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Expiring Assets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {expiringAssets.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">Nothing expiring in the next 60 days</p>
              ) : expiringAssets.map((asset, index) => (
                <div key={`${asset.type}-${asset.id || index}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/20" data-testid={`asset-${index}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {getAssetIcon(asset.type)}
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{asset.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{asset.client_name || '—'}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium text-destructive">{formatDate(asset.expiry_date)}</p>
                    <p className="text-xs text-muted-foreground capitalize">{asset.type}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      <TransactionFormDialog
        open={txnDialogOpen}
        onClose={() => setTxnDialogOpen(false)}
        initialValues={{ type: 'credit' }}
        onSaved={() => fetchDashboardData(dateRange)}
      />
    </div>
  );
};
